import { ReactElement } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../layouts/PublicLayout';

type Provision = {
    heading: string;
    body?: string;
    intro?: string;
    list?: string[];
};

function PrivacyPolicy(): ReactElement {
    const { t, i18n } = useTranslation('privacy');
    const metaTitle = t('meta.title');
    const metaDescription = t('meta.description');
    const pageTitle = t('title');
    const provisions = t('provision', { returnObjects: true }) as Record<string, Provision>;
    const buttonLabel = t('button');

    const currentLang = (i18n.language || 'ja').split('-')[0];
    const htmlLang = currentLang === 'ja' ? 'ja' : 'en';
    const brandName = htmlLang === 'en' ? 'Kotomil' : 'コトミル';
    const baseUrl = 'https://kotomil.com/privacypolicy';
    const canonicalUrl = currentLang === 'en' ? `${baseUrl}?lang=en` : baseUrl;
    const ogLocale = htmlLang === 'ja' ? 'ja_JP' : 'en_US';
    const ogAlternateLocale = htmlLang === 'ja' ? 'en_US' : 'ja_JP';
    const alternateLinks = [
        { lang: 'ja', href: baseUrl },
        { lang: 'en', href: `${baseUrl}?lang=en` },
        { lang: 'x-default', href: baseUrl },
    ];

    return (
        <PublicLayout>
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />
                {alternateLinks.map(({ lang, href }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={href} />
                ))}

                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content={brandName} />
                <meta
                    property="og:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:locale" content={ogLocale} />
                <meta property="og:locale:alternate" content={ogAlternateLocale} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@KotomilApp" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta
                    name="twitter:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
            </Helmet>

            <div className="max-w-4xl mx-auto px-4 py-8">
                <h1 className="text-center text-3xl font-bold my-10">{pageTitle}</h1>

                <div className="prose prose-lg max-w-none mx-auto">
                    {Object.entries(provisions).map(([key, provision]) => (
                        <section key={key} className="mb-8">
                            <h2 className="text-xl font-semibold mb-4">{provision.heading}</h2>
                            {provision.body && (
                                <p
                                    className="mb-4"
                                    dangerouslySetInnerHTML={{ __html: provision.body }}
                                />
                            )}
                            {provision.intro && <p className="mb-2">{provision.intro}</p>}
                            {provision.list && (
                                <ul className="list-disc pl-6 mb-4 space-y-1">
                                    {provision.list.map((item, index) => (
                                        <li key={`${key}-item-${index}`} dangerouslySetInnerHTML={{ __html: item }} />
                                    ))}
                                </ul>
                            )}
                        </section>
                    ))}
                </div>

                <div className="text-center py-10">
                    <Link to="/">
                        <button
                            type="button"
                            className="inline-block px-8 py-3 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200 w-full max-w-md"
                        >
                            {buttonLabel}
                        </button>
                    </Link>
                </div>
            </div>
        </PublicLayout>
    );
}

export default PrivacyPolicy;
